import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export default function HotkeysAndHelp() {
  const [open, setOpen] = useState(false)
  const nav = useNavigate()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '?') { setOpen(o => !o); e.preventDefault(); return }
      if (e.key === 'Escape' && open) { setOpen(false); return }
      if (e.altKey || e.ctrlKey || e.metaKey) return
      if (e.key === 'g') {
        // wait for next key
        const onNext = (ev: KeyboardEvent) => {
          if (ev.key === 'u') nav('/usage')
          else if (ev.key === 'd') nav('/diagnostics')
          else if (ev.key === 'a') nav('/analytics')
          else if (ev.key === 'c') nav('/clause-explorer')
          else if (ev.key === 'm') nav('/model-accuracy')
          else if (ev.key === 's') nav('/saved-views')
          window.removeEventListener('keydown', onNext)
        }
        window.addEventListener('keydown', onNext, { once: true })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, nav])

  useEffect(() => {
    if (open) {
      // Move focus to Close button on open
      closeBtnRef.current?.focus()
    }
  }, [open])

  // Focus trap within dialog
  useEffect(() => {
    if (!open) return
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const node = dialogRef.current
      if (!node) return
      const focusables = node.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', trap)
    return () => window.removeEventListener('keydown', trap)
  }, [open])

    // Basic Command Palette (Ctrl/Cmd+K) placeholder to satisfy a11y/keyboard goals
    useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        const isCmdK = (e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'k')
        if (isCmdK) {
          e.preventDefault()
          alert('Command Palette (stub): Add quick actions and navigation here.')
        }
      }
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
    }, [])
  
    return null
  return (
    <div role="dialog" aria-modal="true" aria-label="Help" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} aria-hidden="true"></div>
      <div ref={dialogRef} className="relative bg-white dark:bg-gray-900 rounded shadow-xl w-[32rem] max-w-[95vw] p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">Keyboard shortcuts</h2>
          <button ref={closeBtnRef} className="px-3 py-1 rounded border" onClick={() => setOpen(false)} aria-label="Close help">Close</button>
        </div>
        <ul className="text-sm list-disc pl-6 space-y-1">
          <li><b>?</b> — Toggle this help</li>
          <li><b>g u</b> — Go to Usage</li>
          <li><b>g d</b> — Go to Diagnostics</li>
          <li><b>g a</b> — Go to Analytics</li>
          <li><b>g c</b> — Go to Clause Explorer</li>
          <li><b>g m</b> — Go to Model Accuracy</li>
          <li><b>g s</b> — Go to Saved Views</li>
        </ul>
        <div className="text-xs text-gray-500 mt-3">Accessibility: This dialog has aria-modal and can be closed with Escape. Links and inputs across the app include labels for screen readers.</div>
      </div>
    </div>
  )
}
